import React, { useMemo } from "react";
import { Heart, Space } from "lucide-react";
import { PixelSprite } from "@/components/PixelSprite";
import { LIVES_PER_LEVEL } from "@/game/gameData";
import { matchesPrefix } from "@/game/logic";

export function Label({ label, typed, light = false, norm }) {
  const rest = light ? "text-white" : "text-[#39ff14]";
  if (!matchesPrefix(label, typed, norm)) return <span className={rest}>{label}</span>;
  const n = typed.trim().length;
  return (
    <>
      <span className="text-[#ffe600] underline decoration-[#ff007f]">{label.slice(0, n)}</span>
      <span className={rest}>{label.slice(n)}</span>
    </>
  );
}

const labelBoxClass = (a) => {
  if (a.rainbow) return "tb-rainbow-border rounded px-2 py-0.5 shadow-[0_0_12px_rgba(255,0,127,0.5)]";
  if (a.missile) return "bg-[#1a0d0dcc] border border-[#ff6a00]/70 rounded px-2 py-0.5 shadow-[0_0_10px_rgba(255,106,0,0.4)]";
  return "bg-[#0d0d1acc] border border-[#39ff14]/60 rounded px-2 py-0.5 shadow-[0_0_10px_rgba(57,255,20,0.35)]";
};

export function Alien({ a, typed, norm }) {
  return (
    <div
      className="absolute z-10 flex flex-col items-center gap-1"
      style={{ left: `${a.x}%`, top: `${a.y * 100}%`, transform: "translate(-50%,-50%)" }}
      data-testid="alien"
      data-label={a.label}
      data-rainbow={a.rainbow || undefined}
      data-missile={a.missile || undefined}
    >
      {a.missile ? (
        <div className="tb-missile-flicker">
          <PixelSprite missile color="#ff6a00" px={3} />
        </div>
      ) : (
        <div className={a.rainbow ? "tb-float tb-rainbow" : "tb-float"}>
          <PixelSprite id={a.id} color={a.rainbow ? "#ff007f" : "#39ff14"} px={4} />
        </div>
      )}
      <div className={labelBoxClass(a)}>
        <span className="font-typewriter text-base sm:text-lg font-bold tracking-widest text-glow-green">
          <Label label={a.label} typed={typed} light={a.rainbow || a.missile} norm={norm} />
        </span>
      </div>
    </div>
  );
}

export function BossSprite({ boss, kind, typed, norm }) {
  return (
    <div
      className="absolute z-10 flex flex-col items-center gap-1"
      data-testid="boss-sprite"
      data-boss={kind.key}
      style={{
        left: `${boss.x}%`,
        top: `${boss.y * 100}%`,
        transform: "translate(-50%,-50%)",
        filter: boss.flash > 0 ? "brightness(2.5)" : "none",
      }}
    >
      <div className="tb-float">
        <PixelSprite id="boss" color={kind.color} px={7} boss={kind.key} />
      </div>
      {boss.stage === "attack" && (
        <div
          className="bg-[#0d0d1acc] border rounded px-2 py-0.5"
          style={{ borderColor: kind.color, boxShadow: `0 0 10px ${kind.color}66` }}
          data-testid="boss-target-label"
        >
          <span className="font-typewriter text-base sm:text-lg font-bold tracking-widest">
            <Label label={boss.tokens[boss.hit] || ""} typed={typed} norm={norm} />
          </span>
        </div>
      )}
    </div>
  );
}

const tokenClass = (i, hit) => {
  if (i < hit) return "text-slate-500 line-through mr-1";
  if (i === hit) return "text-[#ffe600] font-bold mr-1";
  return "text-slate-300/80 mr-1";
};

export function BossHud({ boss, kind }) {
  if (boss.stage !== "attack") {
    return (
      <div
        className="absolute top-2 right-2 bg-[#2a0f0f]/70 border border-[#ff6a00]/80 rounded-lg px-3 py-2 z-30 pointer-events-none"
        data-testid="boss-missile-warning"
      >
        <div className="font-arcade text-[8px] text-[#ff6a00] tracking-widest">
          {boss.stage === "enter" ? `${kind.name} INCOMING` : "MISSILES!"}
        </div>
        {boss.stage === "missiles" && (
          <div className="font-typewriter text-xs text-slate-300 mt-1">
            {kind.missiles - boss.fired} to launch · shoot them all
          </div>
        )}
      </div>
    );
  }
  const left = boss.tokens.length - boss.hit;
  return (
    <div
      className="absolute top-2 right-2 w-[38%] max-w-[220px] bg-[#1c1333]/60 backdrop-blur-[2px] border rounded-lg p-2 z-30 pointer-events-none"
      style={{ borderColor: kind.color }}
      data-testid="boss-hud"
    >
      <div className="flex items-center justify-between mb-1">
        <span className="font-arcade text-[8px] tracking-widest" style={{ color: kind.color }}>
          {kind.name}
        </span>
        <span className="font-typewriter text-xs text-slate-300">{left} left</span>
      </div>
      <div className="font-typewriter text-[10px] text-[#ff2a2a] mb-1">Don't let it reach you — instant game over!</div>
      <div className="w-full h-3 bg-[#0b0b18] rounded-full overflow-hidden border" style={{ borderColor: `${kind.color}80` }}>
        <div
          className="h-full transition-all duration-200"
          style={{ width: `${(left / boss.tokens.length) * 100}%`, background: "linear-gradient(90deg,#ff007f,#ff2a2a)" }}
        />
      </div>
      <div className="mt-1.5 font-typewriter text-xs sm:text-sm tracking-wide break-words leading-snug">
        {boss.tokens.map((t, i) => (
          <span key={`${i}-${t}`} className={tokenClass(i, boss.hit)}>
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

export function Hud({ s, level, practice, onFinishPractice }) {
  return (
    <div
      className="flex items-center justify-between px-3 py-2 bg-[#0d0e1a]/90 border-b-2 border-[#2f2c5a] z-20"
      data-testid="game-hud"
    >
      <div className={`${practice ? "bg-[#ffb000] text-[#06060e]" : "bg-[#ff007f] text-white"} px-2 py-1 rounded font-arcade text-[9px] tracking-wider`}>
        {practice ? "PRACTICE " : ""}LVL {level.id}
      </div>
      {practice ? (
        <div className="flex items-center gap-3 font-vt text-lg" data-testid="hud-practice-stats">
          <span className="text-[#39ff14]">HIT {s.destroyed}</span>
          <span className="text-[#ffb000]">MISSED {s.escaped}</span>
          <button
            data-testid="end-practice-btn"
            onClick={onFinishPractice}
            className="font-arcade text-[8px] border border-[#ffb000] text-[#ffb000] rounded px-2 py-1 hover:bg-[#ffb000] hover:text-[#06060e] transition-colors"
          >
            FINISH · ESC
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1" data-testid="hud-lives">
          {Array.from({ length: LIVES_PER_LEVEL }, (_, i) => (
            <Heart
              key={`life-${i}`}
              size={16}
              className={i < s.lives ? "text-[#ff2a2a] fill-[#ff2a2a]" : "text-[#3a2030] fill-[#1a1020]"}
            />
          ))}
        </div>
      )}
      <div data-testid="hud-score" className="font-vt text-2xl text-cyan-400 text-glow-cyan tracking-widest">
        {String(s.score).padStart(6, "0")}
      </div>
    </div>
  );
}

export function InputLine({ typed }) {
  return (
    <div
      className="relative z-30 w-full bg-[#121124] border-t-4 border-[#00f0ff] px-4 py-3 flex items-center gap-3 shadow-[0_-10px_25px_rgba(0,240,255,0.15)]"
      data-testid="typewriter-input"
    >
      <span className="font-arcade text-[9px] text-slate-500 hidden sm:block">TYPE&gt;</span>
      <div className="flex-1 border-b-2 border-[#00f0ff]/40 h-10 flex items-center overflow-hidden">
        <span
          data-testid="typed-text"
          className="font-typewriter text-xl sm:text-2xl leading-none font-bold tracking-widest text-cyan-300 text-glow-cyan whitespace-pre"
        >
          {typed}
        </span>
        <PixelSprite ship color="#00f0ff" px={2} className="tb-cursor" />
      </div>
      <div className="flex items-center gap-1.5 bg-[#1c1a36] border-2 border-[#39ff14] text-[#39ff14] rounded-lg px-3 py-1.5">
        <Space size={16} />
        <span className="font-arcade text-[8px] tracking-wider hidden sm:inline">SPACE = FIRE</span>
      </div>
    </div>
  );
}

export function Explosion({ x, y, color, text }) {
  const bits = useMemo(
    () =>
      Array.from({ length: 8 }, (_, i) => ({
        id: `bit-${i}`,
        dx: (Math.random() - 0.5) * 90,
        dy: (Math.random() - 0.5) * 90,
        c: Math.random() < 0.5 ? color : "#ffe600",
      })),
    [color]
  );
  return (
    <div
      className="absolute z-20 pointer-events-none"
      style={{ left: `${x}%`, top: `${y * 100}%`, transform: "translate(-50%,-50%)" }}
    >
      <div
        className="tb-boom rounded-full"
        style={{ width: 30, height: 30, background: `radial-gradient(circle, ${color}, transparent 70%)` }}
      />
      {text && (
        <span
          className="absolute -top-6 left-1/2 -translate-x-1/2 font-arcade text-[10px] text-[#ffe600] whitespace-nowrap tb-slidedown"
          style={{ textShadow: "0 0 8px #ff007f" }}
        >
          {text}
        </span>
      )}
      {bits.map((b) => (
        <span
          key={b.id}
          className="tb-debris"
          style={{
            background: b.c,
            top: 12,
            left: 12,
            "--dx": `${b.dx}px`,
            "--dy": `${b.dy}px`,
            animation: "tb-debris-fly 0.5s ease-out forwards",
          }}
        />
      ))}
    </div>
  );
}
